import { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, FileText, Download, Printer, Mail, Send } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AccountStatementModal = ({ open, onClose, customer }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [includeCashSales, setIncludeCashSales] = useState(false);
  const [sendingStatement, setSendingStatement] = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState(null);
  const [reversingRef, setReversingRef] = useState(null);

  useEffect(() => {
    if (open && customer) {
      fetchStatement();
    }
  }, [open, customer, startDate, endDate, includeCashSales]);

  const fetchStatement = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/customers/${customer.id}/statement?start_date=${startDate}&end_date=${endDate}&include_cash_sales=${includeCashSales}`
      );
      setData(response.data);
    } catch (error) {
      console.error("Error fetching statement:", error);
      toast.error("Failed to load statement");
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = () => {
    if (!data || data.transactions.length === 0) {
      toast.error("No data to export");
      return;
    }

    const headers = ["DocType", "InvNo", "Debit", "Credit", "Balance", "Paid", "Date", "User", "Description"];
    const csvContent = [
      headers.join(","),
      ...data.transactions.map(tx => [
        tx.doc_type,
        tx.inv_no,
        tx.debit.toFixed(2),
        tx.credit.toFixed(2),
        tx.balance.toFixed(2),
        tx.paid.toFixed(2),
        tx.date,
        tx.user,
        `"${tx.description}"`
      ].join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `statement_${customer.name}_${startDate}_${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Export completed");
  };

  const handlePrint = () => {
    if (!data) return;

    const printContent = `
      <html>
        <head>
          <title>Account Statement - ${customer.name}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { font-size: 18px; margin-bottom: 5px; }
            h2 { font-size: 14px; color: #666; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; font-size: 10px; }
            th, td { border: 1px solid #ddd; padding: 5px; text-align: left; }
            th { background: #f5f5f5; font-weight: bold; }
            .number { text-align: right; }
            .debit { color: #dc2626; }
            .credit { color: #16a34a; }
            .aging { margin-top: 20px; display: flex; gap: 20px; }
            .aging-box { border: 1px solid #ddd; padding: 10px; text-align: center; min-width: 80px; }
            .aging-label { font-size: 11px; color: #666; }
            .aging-value { font-size: 14px; font-weight: bold; }
          </style>
        </head>
        <body>
          <h1>ACCOUNT STATEMENT</h1>
          <h2>${customer.name} | Account: ${customer.account_number}</h2>
          <p>Period: ${startDate} to ${endDate}</p>
          
          <table>
            <thead>
              <tr>
                <th>DocType</th>
                <th>InvNo</th>
                <th class="number">Debit</th>
                <th class="number">Credit</th>
                <th class="number">Balance</th>
                <th class="number">Paid</th>
                <th>Date</th>
                <th>User</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              ${data.transactions.map(tx => `
                <tr>
                  <td>${tx.doc_type}</td>
                  <td>${tx.inv_no}</td>
                  <td class="number debit">${tx.debit > 0 ? 'R ' + tx.debit.toFixed(2) : '-'}</td>
                  <td class="number credit">${tx.credit > 0 ? 'R ' + tx.credit.toFixed(2) : '-'}</td>
                  <td class="number">R ${tx.balance.toFixed(2)}</td>
                  <td class="number">${tx.paid > 0 ? 'R ' + tx.paid.toFixed(2) : '-'}</td>
                  <td>${tx.date}</td>
                  <td>${tx.user}</td>
                  <td>${tx.description}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <div class="aging">
            <div class="aging-box">
              <div class="aging-label">30 Days</div>
              <div class="aging-value">R ${Math.abs(data.aging.days_30).toFixed(2)}</div>
            </div>
            <div class="aging-box">
              <div class="aging-label">60 Days</div>
              <div class="aging-value">R ${Math.abs(data.aging.days_60).toFixed(2)}</div>
            </div>
            <div class="aging-box">
              <div class="aging-label">90 Days</div>
              <div class="aging-value">R ${Math.abs(data.aging.days_90).toFixed(2)}</div>
            </div>
            <div class="aging-box">
              <div class="aging-label">120 Days</div>
              <div class="aging-value">R ${Math.abs(data.aging.days_120).toFixed(2)}</div>
            </div>
            <div class="aging-box">
              <div class="aging-label">Current</div>
              <div class="aging-value">R ${Math.abs(data.aging.current).toFixed(2)}</div>
            </div>
            <div class="aging-box" style="background: #fef2f2; border-color: #fca5a5;">
              <div class="aging-label">Balance</div>
              <div class="aging-value" style="color: #dc2626;">R ${Math.abs(data.balance_owing).toFixed(2)}</div>
            </div>
          </div>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  // Send full account statement via email
  const handleEmailStatement = async () => {
    if (!customer.email) {
      toast.error("Customer does not have an email address. Please add one in customer details.");
      return;
    }

    setSendingStatement(true);
    try {
      const res = await axios.post(`${API}/email/statement`, {
        customer_id: customer.id,
        recipient_email: customer.email,
        start_date: startDate,
        end_date: endDate,
        include_cash_sales: includeCashSales,
      });
      const data = res.data || {};
      const sent = data.sent_count;
      const total = data.total_recipients;
      const recipients = (data.results || [])
        .filter(r => r.status === 'success')
        .map(r => r.email);
      if (recipients.length > 0) {
        toast.success(`Statement sent to ${recipients.join(', ')}${total && sent < total ? ` (${sent}/${total})` : ''}`);
      } else {
        toast.success(`Statement sent to ${customer.email}`);
      }
    } catch (error) {
      console.error("Error sending statement:", error);
      const errorMessage = error.response?.data?.detail || "Failed to send statement";
      if (errorMessage.includes("disabled")) {
        toast.error("Emails are disabled in system settings. Enable them in Settings > Email.");
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setSendingStatement(false);
    }
  };

  // Send individual invoice via email
  const handleEmailInvoice = async (transaction) => {
    if (!customer.email) {
      toast.error("Customer does not have an email address. Please add one in customer details.");
      return;
    }

    // Only allow emailing invoice-type transactions (PURCHASE means they bought from us)
    if (transaction.doc_type !== 'PURCHASE' && transaction.doc_type !== 'INVOICE') {
      toast.error("Only invoices can be emailed. Payments and credit notes cannot be emailed individually.");
      return;
    }

    const saleId = transaction.sale_id || transaction.id;
    if (!saleId) {
      toast.error("Invoice ID not found. Cannot send email.");
      return;
    }

    setSendingInvoice(saleId);
    try {
      await axios.post(`${API}/email/sale-invoice`, {
        sale_id: saleId,
        recipient_email: customer.email
      });
      toast.success(`Invoice ${transaction.inv_no} sent to ${customer.email}`);
    } catch (error) {
      console.error("Error sending invoice:", error);
      const errorMessage = error.response?.data?.detail || "Failed to send invoice";
      if (errorMessage.includes("disabled")) {
        toast.error("Emails are disabled in system settings. Enable them in Settings > Email.");
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setSendingInvoice(null);
    }
  };

  const handleReverseLoyalty = async (transaction) => {
    const journalRef = transaction.inv_no;
    if (!journalRef) return;
    const confirmed = window.confirm(
      `Reverse loyalty credit ${journalRef} for R ${(transaction.credit || 0).toFixed(2)}?\n\n` +
      `This will post an offsetting DEBIT journal and reduce the customer's loyalty balance. ` +
      `Use this only to undo a duplicate cashback posting.`
    );
    if (!confirmed) return;
    setReversingRef(journalRef);
    try {
      const res = await axios.post(`${API}/loyalty/reverse-credit/${encodeURIComponent(journalRef)}`);
      toast.success(`Reversed ${journalRef} — R ${res.data.amount.toFixed(2)}`);
      // Refresh the statement so the row now shows as 'reversed' and the
      // balance updates immediately.
      await fetchStatement();
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to reverse credit";
      toast.error(msg);
    } finally {
      setReversingRef(null);
    }
  };

  const handleCorrectLoyalty = async (transaction) => {
    const journalRef = transaction.inv_no;
    const current = (transaction.credit || 0).toFixed(2);
    const input = window.prompt(
      `Correct loyalty credit ${journalRef} (now R ${current}).\n\nEnter the RIGHT amount. The statement will simply show the new amount — no reversal lines.`,
      current
    );
    if (input === null) return;
    const newAmount = parseFloat(String(input).replace(",", "."));
    if (Number.isNaN(newAmount) || newAmount < 0) return toast.error("Enter a valid amount");
    const reason = window.prompt("Reason (kept in the internal log only):", "Spend amount was too high");
    if (!reason) return;
    setReversingRef(journalRef);
    const send = (force) => axios.post(`${API}/loyalty/correct-credit/${encodeURIComponent(journalRef)}`, { new_amount: newAmount, reason, force });
    try {
      let res;
      try {
        res = await send(false);
      } catch (e) {
        if (e.response?.status !== 409 || !window.confirm(`${e.response.data.detail}`)) throw e;
        res = await send(true);
      }
      toast.success(`${journalRef} corrected: R ${res.data.old_amount.toFixed(2)} → R ${res.data.new_amount.toFixed(2)}`);
      await fetchStatement();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to correct credit");
    } finally {
      setReversingRef(null);
    }
  };

  const getDocTypeStyle = (docType) => {
    switch (docType) {
      case 'PURCHASE':
        return 'bg-red-100 text-red-700';
      case 'PAYMENT':
        return 'bg-green-100 text-green-700';
      case 'CREDIT NOTE':
        return 'bg-blue-100 text-blue-700';
      case 'OPENING':
        return 'bg-yellow-100 text-yellow-700';
      case 'BALANCE_CORRECTION':
        return 'bg-orange-100 text-orange-700';
      case 'RECEIPT':
        return 'bg-emerald-100 text-emerald-700';
      case 'LATE PENALTY':
        return 'bg-red-50 text-red-800 border border-red-300';
      case 'PENALTY REVERSAL':
        return 'bg-emerald-50 text-emerald-800 border border-emerald-300';
      case 'EARLY-PAY DISC':
        return 'bg-teal-50 text-teal-800 border border-teal-300';
      case 'PAID AT TILL':
        return 'bg-slate-100 text-slate-600 border border-slate-300';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  if (!customer) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <FileText className="h-5 w-5" />
            Account Statement: {customer.name}
          </DialogTitle>
          <div className="text-sm text-gray-500">
            Account: {customer.account_number}
          </div>
        </DialogHeader>

        {/* Date Filters */}
        <div className="flex items-center gap-4 py-2 border-b flex-wrap">
          <div className="flex items-center gap-2">
            <Label className="text-sm">From:</Label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-40"
            />
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm">To:</Label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-40"
            />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={includeCashSales}
              onChange={(e) => setIncludeCashSales(e.target.checked)}
              className="rounded"
              data-testid="include-cash-sales-checkbox"
            />
            <span>Include cash / card sales <span className="text-xs text-slate-500">(paid at till — informational only)</span></span>
          </label>
          <div className="flex-1" />
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleEmailStatement}
            disabled={sendingStatement || !customer.email}
            className="border-blue-300 text-blue-600 hover:bg-blue-50"
            data-testid="email-statement-btn"
          >
            {sendingStatement ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Mail className="h-4 w-4 mr-1" />
            )}
            Email Statement
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportCSV}>
            <Download className="h-4 w-4 mr-1" />
            Export CSV
          </Button>
          <Button variant="outline" size="sm" onClick={handlePrint}>
            <Printer className="h-4 w-4 mr-1" />
            Print
          </Button>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : !data ? (
            <div className="flex items-center justify-center h-64 text-gray-500">
              No data available
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>DocType</TableHead>
                  <TableHead>InvNo</TableHead>
                  <TableHead className="text-right">Debit</TableHead>
                  <TableHead className="text-right">Credit</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead className="text-right">Paid</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Sort transactions: OPENING first, then by date descending */}
                {data.transactions
                  .slice()
                  .sort((a, b) => {
                    // OPENING always comes first
                    if (a.doc_type === 'OPENING') return -1;
                    if (b.doc_type === 'OPENING') return 1;
                    // Then sort by date descending
                    return (b.date || '').localeCompare(a.date || '');
                  })
                  .map((tx, idx) => (
                  <TableRow key={tx.id || idx} className={
                    tx.doc_type === 'OPENING' ? 'bg-yellow-50' :
                    tx.paid_at_till ? 'bg-slate-50 text-slate-600' :
                    ''
                  }>
                    <TableCell>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getDocTypeStyle(tx.doc_type)}`}>
                        {tx.doc_type}
                      </span>
                    </TableCell>
                    <TableCell className="font-medium">{tx.inv_no || '-'}</TableCell>
                    <TableCell className="text-right text-red-600 font-medium">
                      {tx.paid_at_till
                        ? <span className="text-slate-500 italic">R {(tx.paid || 0).toFixed(2)}</span>
                        : (tx.debit > 0 ? `R ${tx.debit.toFixed(2)}` : '-')}
                    </TableCell>
                    <TableCell className="text-right text-green-600 font-medium">
                      {tx.credit > 0 ? `R ${tx.credit.toFixed(2)}` : '-'}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {tx.paid_at_till ? <span className="text-slate-400 italic">—</span> : `R ${tx.balance.toFixed(2)}`}
                    </TableCell>
                    <TableCell className="text-right">
                      {tx.paid > 0 ? `R ${tx.paid.toFixed(2)}` : '-'}
                    </TableCell>
                    <TableCell>{tx.date}</TableCell>
                    <TableCell className="text-gray-500">{tx.user}</TableCell>
                    <TableCell className="max-w-[200px] truncate" title={tx.description}>
                      {tx.description}
                    </TableCell>
                    <TableCell className="text-center">
                      {(tx.doc_type === 'PURCHASE' || tx.doc_type === 'INVOICE') && tx.sale_id && customer.email ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEmailInvoice(tx)}
                          disabled={sendingInvoice === tx.sale_id}
                          className="h-7 px-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                          title={`Email invoice ${tx.inv_no} to ${customer.email}`}
                          data-testid={`email-invoice-${tx.inv_no}`}
                        >
                          {sendingInvoice === tx.sale_id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Send className="h-3 w-3" />
                          )}
                        </Button>
                      ) : tx.doc_type === 'CREDIT NOTE' && (tx.description || '').toLowerCase().includes('loyalty cashback') && !tx.reversed ? (
                        <div className="flex items-center justify-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCorrectLoyalty(tx)}
                          disabled={reversingRef === tx.inv_no}
                          className="h-7 px-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                          title={`Correct the amount of ${tx.inv_no} (no reversal lines on the statement)`}
                          data-testid={`correct-loyalty-${tx.inv_no}`}
                        >
                          <span className="text-[11px] font-semibold">✎ Correct</span>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleReverseLoyalty(tx)}
                          disabled={reversingRef === tx.inv_no}
                          className="h-7 px-2 text-red-600 hover:text-red-800 hover:bg-red-50"
                          title={`Reverse duplicate loyalty credit ${tx.inv_no}`}
                          data-testid={`reverse-loyalty-${tx.inv_no}`}
                        >
                          {reversingRef === tx.inv_no ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <span className="text-[11px] font-semibold">↺ Reverse</span>
                          )}
                        </Button>
                        </div>
                      ) : tx.reversed ? (
                        <span className="text-[10px] text-gray-400 italic">reversed</span>
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Aging Buckets */}
        {data && (
          <div className="border-t pt-4 mt-2">
            <div className="grid grid-cols-7 gap-2 text-center">
              <div className="p-3 bg-gray-50 rounded">
                <div className="text-xs text-gray-500">30 Days</div>
                <div className="text-lg font-bold">R {Math.abs(data.aging.days_30).toFixed(2)}</div>
              </div>
              <div className="p-3 bg-gray-50 rounded">
                <div className="text-xs text-gray-500">60 Days</div>
                <div className="text-lg font-bold">R {Math.abs(data.aging.days_60).toFixed(2)}</div>
              </div>
              <div className="p-3 bg-gray-50 rounded">
                <div className="text-xs text-gray-500">90 Days</div>
                <div className="text-lg font-bold">R {Math.abs(data.aging.days_90).toFixed(2)}</div>
              </div>
              <div className="p-3 bg-gray-50 rounded">
                <div className="text-xs text-gray-500">120 Days</div>
                <div className="text-lg font-bold">R {Math.abs(data.aging.days_120).toFixed(2)}</div>
              </div>
              <div className="p-3 bg-blue-50 rounded">
                <div className="text-xs text-blue-600">Current</div>
                <div className="text-lg font-bold text-blue-700">R {Math.abs(data.aging.current).toFixed(2)}</div>
              </div>
              <div className="p-3 bg-gray-100 rounded">
                <div className="text-xs text-gray-500">Total Debit</div>
                <div className="text-lg font-bold text-red-600">R {Math.abs(data.total_debit).toFixed(2)}</div>
              </div>
              <div className={`p-3 rounded border ${data.balance_owing >= 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                <div className={`text-xs ${data.balance_owing >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {data.balance_owing >= 0 ? 'Balance Owing' : 'Balance Credit'}
                </div>
                <div className={`text-xl font-bold ${data.balance_owing >= 0 ? 'text-red-700' : 'text-green-700'}`}>
                  R {data.balance_owing >= 0 ? '' : '-'}{Math.abs(data.balance_owing).toFixed(2)}
                </div>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AccountStatementModal;
